import { useState, useEffect } from "react";
import { Button, LoadingSpinner, InlineAlert } from "../../components/BaseComponents";
import CanonicalModal from "../../components/BaseComponents/CanonicalModal/CanonicalModal";
import Input from "../../components/BaseComponents/Forms/Input";
import useApi from "../../hooks/useApi";
import { getApiErrorMessage, getCSRFToken } from "../../lib/utils";

type EditPortfolioModalProps = {
    onClose: () => void,
    onEdited: (newId: string) => void
    isOpen?: boolean,
    portfolioId: string,
    portfolioName: string;
};

function EditPortfolioModal({onClose, onEdited, isOpen, portfolioId, portfolioName}: EditPortfolioModalProps) {
    const [label, setLabel] = useState(portfolioName);
    const api = useApi();

    const [loading, setLoading] = useState(false)
    const [error, setError] = useState('')

    // Sync prop changes to state
    useEffect(() => {
        setLabel(portfolioName)
    }, [portfolioName])

    // Issue #884: this PUT is behind RequireWriteAccess, so it can now reject with
    // READ_ONLY_ACCESS/ACCESS_AUDIT_UNAVAILABLE (#857 item 6) as well as any generic failure.
    // setLoading(false) previously only ran after the awaited call succeeded, so a rejection left
    // `loading` stuck true forever - and since `actions` rendered null while loading, the Cancel
    // button disappeared too, trapping the user with only a spinner and no visible error.
    const edit = async () => {
        setLoading(true)
        setError('')
        try {
            const result = await api.put(`/portfolios/${portfolioId}`, {
                name: label,
                _csrf: await getCSRFToken(),
            })
            onEdited(result.data.id);
        } catch (err: any) {
            setError(getApiErrorMessage(err, 'Failed to save this portfolio. Please try again.'));
        } finally {
            setLoading(false);
        }
    }

    return (
        <CanonicalModal
            open={!!isOpen}
            onClose={onClose}
            size="small"
            title="Edit portfolio"
            actions={
                loading ? null : (
                    <>
                        <Button onClick={edit} additionalClass="mr-5" disabled={!label} type="Primary" text="Save portfolio" />
                        <Button onClick={onClose} type="Outlined" text="Cancel" />
                    </>
                )
            }
        >
            { loading  ? (
                <LoadingSpinner />
            ): (
                <>
                    <Input
                        label="Portfolio name"
                        name="portfolioName"
                        additionalClass="mt-8"
                        value={label}
                        onChange={e => setLabel(e.target.value)} type="text"
                        placeholder="Portfolio name..."
                    />
                    { error && (
                        <InlineAlert type='error' className='mt-4'>{ error }
                        </InlineAlert>
                    )}
                </>
            )}
        </CanonicalModal>
    )
}

export default EditPortfolioModal;
